/**
 * Iter 238 (Lot 3 polish a) — Carte Health Check synthétique pour Dashboard admin.
 *
 * Affiche un mini résumé live des intégrations API (pulse coloré + 4 chiffres)
 * + lien vers la page complète /admin/integrations-health.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Activity, CheckCircle2, AlertTriangle, XCircle, Plug, ChevronRight } from "lucide-react";

const STATUS_STYLES = {
  ok: {
    border: "border-emerald-500/20 hover:border-emerald-500/40",
    text: "text-emerald-400",
    pulse: "bg-emerald-400",
  },
  warning: {
    border: "border-amber-500/20 hover:border-amber-500/40",
    text: "text-amber-400",
    pulse: "bg-amber-400",
  },
  ko: {
    border: "border-rose-500/20 hover:border-rose-500/40",
    text: "text-rose-400",
    pulse: "bg-rose-400",
  },
};

export default function HealthCheckMiniCard() {
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !["admin", "superadmin", "tenant_admin"].includes(user.role)) {
      setLoading(false);
      return;
    }
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/integrations/health");
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const i = setInterval(load, 90000);
    return () => { alive = false; clearInterval(i); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  if (!["admin", "superadmin", "tenant_admin"].includes(user?.role)) return null;
  if (loading) return null;
  if (!data) return null;

  const s = data.summary || {};
  const total = s.total || 0;
  const ok = s.ok || 0;
  const warning = s.warning || 0;
  const ko = s.ko || 0;
  const notConfig = s.not_configured || 0;

  const overall = ko > 0 ? "ko" : warning > 0 ? "warning" : "ok";
  const styles = STATUS_STYLES[overall];

  return (
    <button
      onClick={() => navigate("/admin/integrations-health")}
      className={`w-full bg-[#14101c] border ${styles.border} rounded-xl p-4 transition-colors text-left group`}
      data-testid="dashboard-health-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Activity className={`w-4 h-4 ${styles.text}`} />
            <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${styles.pulse} animate-pulse`} />
          </div>
          <h3 className="text-sm font-bold text-white">Contrôle API</h3>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Tile Icon={CheckCircle2} value={ok} label="OK" color="text-emerald-400" />
        <Tile Icon={AlertTriangle} value={warning} label="Avert." color="text-amber-400" />
        <Tile Icon={XCircle} value={ko} label="Erreur" color="text-rose-400" />
        <Tile Icon={Plug} value={notConfig} label="Non conf." color="text-slate-400" />
      </div>
      <div className="text-[10px] text-slate-500 mt-3">
        {total} intégrations scannées · Voir le détail →
      </div>
    </button>
  );
}

function Tile({ Icon, value, label, color }) {
  return (
    <div className="bg-[#0b0811] rounded-lg p-2 text-center">
      <div className={`flex items-center justify-center gap-1 ${color}`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="text-base font-black">{value}</span>
      </div>
      <div className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}
